<template>
    <v-container class="mt-10 pb-12"
        style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">
        <!-- Header Section -->
        <div class="mb-6">
            <v-btn text class="mb-4 text-none px-0" @click="goBack">
                <v-icon left small>mdi-arrow-left</v-icon>
                Back to Directory
            </v-btn>

            <div v-if="user" class="d-flex align-center mb-2">
                <v-avatar color="primary" size="56" class="white--text text-h6 font-weight-medium mr-4">
                    <v-img v-if="user.profile_picture" :src="getProfilePictureUrl(user.profile_picture)"></v-img>
                    <span v-else>{{ getUserInitials(user) }}</span>
                </v-avatar>
                <div>
                    <h1 class="text-h4 font-weight-bold mb-0">{{ getUserFullName(user) }} · KPIs</h1>
                    <p class="text-subtitle-1 grey--text mb-0">
                        {{ user.position_name || user.role?.name || 'No Position' }}
                    </p>
                </div>
            </div>
        </div>

        <!-- No permission -->
        <v-alert v-if="!loading && !canViewUserTasks" type="info" outlined>
            <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-lock</v-icon>
                <span>KPIs are private for this user.</span>
            </div>
        </v-alert>

        <!-- User not found -->
        <v-alert v-else-if="!loading && !user" type="warning" outlined>
            User not found.
        </v-alert>

        <template v-else>
            <!-- Stat Tiles -->
            <v-row class="mb-6">
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4 text-center position-relative" elevation="1">
                        <v-overlay :value="loading" absolute>
                            <v-progress-circular indeterminate size="32" width="2" color="primary" />
                        </v-overlay>
                        <div class="d-flex align-center justify-center mb-2">
                            <v-icon class="mr-2" color="primary">mdi-format-list-bulleted</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Total Tasks</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ totalTasks }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4 text-center position-relative" elevation="1">
                        <v-overlay :value="loading" absolute>
                            <v-progress-circular indeterminate size="32" width="2" color="orange" />
                        </v-overlay>
                        <div class="d-flex align-center justify-center mb-2">
                            <v-icon class="mr-2" color="orange">mdi-progress-clock</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">In Progress</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ inProgressCount }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4 text-center position-relative" elevation="1">
                        <v-overlay :value="loading" absolute>
                            <v-progress-circular indeterminate size="32" width="2" color="success" />
                        </v-overlay>
                        <div class="d-flex align-center justify-center mb-2">
                            <v-icon class="mr-2" color="success">mdi-check-circle</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Completed</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ completedCount }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4 text-center position-relative" elevation="1">
                        <v-overlay :value="loading" absolute>
                            <v-progress-circular indeterminate size="32" width="2" color="error" />
                        </v-overlay>
                        <div class="d-flex align-center justify-center mb-2">
                            <v-icon class="mr-2" color="error">mdi-alert-circle</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Overdue</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ overdueCount }}</div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <!-- Completion Rate -->
                <v-col cols="12" md="6">
                    <v-card elevation="0" outlined class="pa-6 h-100">
                        <div class="d-flex align-center justify-space-between mb-2">
                            <span class="text-subtitle-1 font-weight-bold">Completion Rate</span>
                            <span class="text-h5 font-weight-bold">{{ completionRate }}%</span>
                        </div>
                        <v-progress-linear :value="completionRate" height="10" rounded color="success" />
                        <p class="text-caption grey--text mt-2 mb-0">
                            {{ completedCount }} of {{ totalTasks }} tasks completed
                        </p>
                    </v-card>
                </v-col>

                <!-- Avg Completion Time -->
                <v-col cols="12" md="6">
                    <v-card elevation="0" outlined class="pa-6 h-100">
                        <div class="d-flex align-center mb-2">
                            <v-icon class="mr-2" color="primary">mdi-timer-outline</v-icon>
                            <span class="text-subtitle-1 font-weight-bold">Avg. Completion Time</span>
                        </div>
                        <div class="text-h3 font-weight-bold">
                            {{ avgCompletionDays !== null ? avgCompletionDays : '—' }}
                            <span v-if="avgCompletionDays !== null" class="text-h6 font-weight-regular">days</span>
                        </div>
                        <p class="text-caption grey--text mt-2 mb-0">
                            Based on {{ completedWithDatesCount }} completed task{{ completedWithDatesCount === 1 ? '' : 's' }} with timestamps
                        </p>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Priority Breakdown -->
            <v-card elevation="0" outlined class="pa-6 mt-6">
                <span class="text-subtitle-1 font-weight-bold">Open Tasks by Priority</span>
                <div v-if="!openTasksCount" class="text-body-2 grey--text mt-4">No open tasks.</div>
                <div v-else class="mt-4">
                    <div v-for="row in priorityBreakdown" :key="row.name" class="mb-3">
                        <div class="d-flex align-center justify-space-between mb-1">
                            <span class="text-body-2 font-weight-medium">{{ row.name }}</span>
                            <span class="text-body-2 grey--text">{{ row.count }}</span>
                        </div>
                        <v-progress-linear :value="row.percent" height="8" rounded :color="row.color" />
                    </div>
                </div>
            </v-card>
        </template>
    </v-container>
</template>

<script>
export default {
    name: 'UserKpisComponent',
    inject: ['users'],
    data() {
        return {
            loading: false,
            tasks: [],
        };
    },
    computed: {
        get_user() {
            return this.$store.getters['auth/get_user'];
        },
        userId() {
            return Number(this.$route.params.userId);
        },
        user() {
            return (this.users() || []).find(u => u.id === this.userId) || null;
        },
        canViewUserTasks() {
            const currentUser = this.get_user;
            if (!currentUser) return false;

            // Admin/manager/super admin can view anyone's KPIs
            if ([1, 2, 11].includes(currentUser.role_id)) return true;

            // Users can always view their own KPIs
            if (currentUser.id === this.userId) return true;

            // Mirrors the same privacy restriction used in the directory dialog
            const restrictedUserIds = [2, 4];
            if (!restrictedUserIds.includes(this.userId)) return true;
            if (this.userId === 2) return currentUser.id === 2;
            if (this.userId === 4) return currentUser.id === 2 || currentUser.id === 4;
            return false;
        },
        totalTasks() {
            return this.tasks.length;
        },
        completedTasks() {
            return this.tasks.filter(t => t.completed_at || t.status_id === 4);
        },
        completedCount() {
            return this.completedTasks.length;
        },
        inProgressCount() {
            return this.tasks.filter(t => t.status_id === 2).length;
        },
        overdueCount() {
            const now = new Date();
            return this.tasks.filter(t => {
                const isCompleted = t.completed_at || t.status_id === 4;
                return !isCompleted && t.due_date && new Date(t.due_date) < now;
            }).length;
        },
        openTasksCount() {
            return this.totalTasks - this.completedCount;
        },
        completionRate() {
            if (!this.totalTasks) return 0;
            return Math.round((this.completedCount / this.totalTasks) * 100);
        },
        completedWithDatesCount() {
            return this.completedTasks.filter(t => t.completed_at && t.created_at).length;
        },
        avgCompletionDays() {
            const withDates = this.completedTasks.filter(t => t.completed_at && t.created_at);
            if (!withDates.length) return null;
            const totalDays = withDates.reduce((sum, t) => {
                const ms = new Date(t.completed_at) - new Date(t.created_at);
                return sum + Math.max(ms, 0) / (1000 * 60 * 60 * 24);
            }, 0);
            return Math.round((totalDays / withDates.length) * 10) / 10;
        },
        priorityBreakdown() {
            const openTasks = this.tasks.filter(t => !(t.completed_at || t.status_id === 4));
            const counts = {};
            openTasks.forEach(t => {
                const name = t.priority?.name || 'Unset';
                counts[name] = (counts[name] || 0) + 1;
            });
            const colorFor = (name) => {
                const map = { High: 'red', Medium: 'orange', Low: 'green', Unset: 'grey' };
                return map[name] || 'blue-grey';
            };
            const total = openTasks.length || 1;
            return Object.keys(counts)
                .map(name => ({
                    name,
                    count: counts[name],
                    percent: Math.round((counts[name] / total) * 100),
                    color: colorFor(name),
                }))
                .sort((a, b) => b.count - a.count);
        },
    },
    async mounted() {
        document.title = 'DG Tasks - Employee KPIs';
        await this.loadTasks();
    },
    watch: {
        userId() {
            this.loadTasks();
        },
    },
    methods: {
        async loadTasks() {
            if (!this.canViewUserTasks) {
                this.tasks = [];
                return;
            }
            this.loading = true;
            try {
                const response = await this.$axios.get(`api/tasks/get_tasks_by_assignee/${this.userId}`);
                const data = response.data;
                if (Array.isArray(data)) {
                    this.tasks = data;
                } else if (data?.data && Array.isArray(data.data)) {
                    this.tasks = data.data;
                } else {
                    this.tasks = [];
                }
            } catch (error) {
                console.error('Error loading user tasks for KPIs:', error);
                this.$toast.error('Failed to load KPI data');
                this.tasks = [];
            } finally {
                this.loading = false;
            }
        },
        goBack() {
            this.$router.push({ name: 'UserDirectory' }).catch(() => {});
        },
        getUserFullName(user) {
            if (user.first_name || user.last_name) {
                return `${user.first_name || ''} ${user.last_name || ''}`.trim();
            }
            return user.name || 'No Name';
        },
        getUserInitials(user) {
            if (user.first_name && user.last_name) {
                return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
            }
            if (user.name) {
                const parts = user.name.split(' ');
                if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                return user.name[0].toUpperCase();
            }
            return 'NN';
        },
        getProfilePictureUrl(profilePicture) {
            if (!profilePicture) return null;
            if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
                return profilePicture;
            }
            let picturePath = profilePicture;
            if (picturePath.startsWith('/media/')) {
                picturePath = picturePath.replace('/media/', '/storage/');
            }
            const apiUrl = process.env.VUE_APP_API_URL || '';
            const base = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
            picturePath = picturePath.startsWith('/') ? picturePath : `/${picturePath}`;
            return `${base}${picturePath}`;
        },
    },
};
</script>

<style scoped>
.h-100 {
    height: 100%;
}
</style>
